import React, { useState, useCallback, useRef, useMemo, FC, useEffect } from 'react';

import { Worker } from '@react-pdf-viewer/core';
import useFeatureFlags from '../../common/feature-flags/hook';
import { getFileMetadata } from 'src/utils/pdf';
import classNames from 'classnames';
import { FullscreenPreviewModal } from '../FullscreenPreviewModal';
import { PdfViewerComponent } from './viewer-component';
import * as portals from 'react-reverse-portal';

const PDF_WORKER_URL = 'https://unpkg.com/pdfjs-dist@2.6.347/build/pdf.worker.min.js';

type PdfDocumentViewerProps = {
  url: string;
  name: string;
  metadata?: {
    pageWidth?: number;
  };
  isTrackingActive: boolean;
  onDocumentLoad: () => any;
  onPageChange: (index: number, pages: number) => any;
};

export const PdfDocumentViewer: FC<PdfDocumentViewerProps> = ({
  url,
  name,
  metadata,
  onDocumentLoad,
  onPageChange,
}) => {
  const fullscreenModalOpenerRef = useRef<() => void>();
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const [numPages, setNumPages] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const featureFlags = useFeatureFlags();
  const [pageWidth, setPageWidth] = useState(metadata?.pageWidth || 0);
  const portalNode = useMemo(
    () =>
      portals.createHtmlPortalNode({
        attributes: { class: 'w-full h-full' },
      }),
    []
  );
  const [customFullscreenOpen, setCustomFullscreenOpen] = useState(false);

  const openCustomFullscreenCallback = useCallback(() => {
    if (fullscreenModalOpenerRef.current) {
      setCustomFullscreenOpen(true);
      fullscreenModalOpenerRef.current();
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (pageWidth > 0) {
        return;
      }
      const { width = 0 } = (await getFileMetadata(url)) || {};
      setPageWidth(width);
    })();
  }, [url]);

  const onPdfDocumentLoad = useCallback(
    (e) => {
      setNumPages(e.doc._pdfInfo.numPages);
      setLoaded(true);
      if (onDocumentLoad) {
        onDocumentLoad();
      }
    },
    [onDocumentLoad]
  );

  const onPdfPageChange = useCallback(
    (e) => {
      if (onPageChange) {
        // e.currentPage index starts from 0
        onPageChange(e.currentPage, numPages);
      }
    },
    [onPageChange, numPages]
  );

  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      <div
        ref={contentContainerRef}
        className='flex flex-1 flex-col overflow-hidden bg-bedrock-light-gray rounded-lg transform-gpu relative'
      >
        <div className={classNames('flex flex-1 overflow-hidden')}>
          <portals.InPortal node={portalNode}>
            <PdfViewerComponent
              url={url}
              pageWidth={pageWidth}
              enableDownload={featureFlags?.allow_pdf_downloads}
              onDocumentLoad={onPdfDocumentLoad}
              onPageChange={onPdfPageChange}
              openCustomFullscreenCallback={openCustomFullscreenCallback}
              customFullscreenOpen={customFullscreenOpen}
              showControls={loaded}
            />
          </portals.InPortal>
          {!customFullscreenOpen && (
            <portals.OutPortal
              node={portalNode}
              url={url}
              pageWidth={pageWidth}
              enableDownload={featureFlags?.allow_pdf_downloads}
              onDocumentLoad={onPdfDocumentLoad}
              onPageChange={onPdfPageChange}
              openCustomFullscreenCallback={openCustomFullscreenCallback}
              customFullscreenOpen={customFullscreenOpen}
              showControls={loaded}
            />
          )}
          <FullscreenPreviewModal openRef={fullscreenModalOpenerRef} onClose={() => setCustomFullscreenOpen(false)}>
            {customFullscreenOpen && (
              <portals.OutPortal
                node={portalNode}
                url={url}
                pageWidth={pageWidth}
                enableDownload={featureFlags?.allow_pdf_downloads}
                onDocumentLoad={onPdfDocumentLoad}
                onPageChange={onPdfPageChange}
                openCustomFullscreenCallback={openCustomFullscreenCallback}
                customFullscreenOpen={customFullscreenOpen}
                showControls={loaded}
              />
            )}
          </FullscreenPreviewModal>
        </div>
      </div>
    </Worker>
  );
};
